import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { FaEnvelope, FaLock, FaArrowLeft, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useAuth } from '@contexts/AuthContext';
import { useTheme } from '@theme/useTheme';
import ContinueWith from './ContinueWith';

type Mode = 'login' | 'signup' | 'forgot-password' | 'reset-password';

interface LoginSignUpProps {
  closeModal: () => void;
  register?: boolean;
  errors: string[];
  setErrors: (errors: string[]) => void;
}

interface FormData {
  email: string;
  password: string;
  confirmPassword?: string;
  otp?: string;
  newPassword?: string;
}

const LoginSignUp: React.FC<LoginSignUpProps> = ({
  closeModal,
  register = false,
  setErrors,
}) => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [emailFocused, setEmailFocused] = useState(false);
  const {
    login,
    register: registerUser,
    requestPasswordReset,
    resetPassword,
  } = useAuth();
  const [mode, setMode] = useState<Mode>(register ? 'signup' : 'login');
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [resetEmail, setResetEmail] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register: registerField,
    handleSubmit,
    formState: { errors: formErrors },
    watch,
  } = useForm<FormData>();

  const password = watch("password");
  const newPassword = watch("newPassword");

  const isValidEmail = (email: string) => /\S+@\S+\.\S+/.test(email);

  const getEmailBorderColor = () => {
    if (formErrors.email) return colors.error;
    if (emailFocused || watch("email")) return colors.primary;
    return colors.border;
  };


  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    setErrors([]);

    try {
      if (mode === 'login') {
        await login({
          username: data.email,
          password: data.password,
        });
        closeModal();
        navigate('/', { replace: true });
      } else if (mode === 'signup') {
        await registerUser({
          email: data.email,
          password: data.password,
          first_name: data.email.split('@')[0],
          last_name: "",
          mobile: "",
          gender: "Prefer not to say",
          dob: "2000-01-01",
          address: "",
          city: "",
          state: "",
          country: "",
          zip_code: "",
        });
        closeModal();
        navigate('/', { replace: true });
      } else if (mode === 'forgot-password') {
        await requestPasswordReset(data.email);
        setResetEmail(data.email);
        setIsEmailSent(true);
        setMode('reset-password');
      } else if (mode === 'reset-password') {
        await resetPassword(resetEmail, data.otp!, data.newPassword!);
        closeModal();
      }
    } catch (error: unknown) {
      setErrors([error instanceof Error ? error.message : "An error occurred"]);
    } finally {
      setIsLoading(false);
    }
  };

  const getStepTitle = () => {
    switch (mode) {
      case 'login':
        return "Welcome Back";
      case 'signup':
        return "Create Account";
      case 'forgot-password':
        return "Forgot Password?";
      case 'reset-password':
        return "Reset Password";
      default:
        return "Welcome";
    }
  };

  const getStepSubtitle = () => {
    switch (mode) {
      case 'login':
        return "Sign in to your account to continue";
      case 'signup':
        return "Create a new account to get started";
      case 'forgot-password':
        return "Enter your email to reset your password";
      case 'reset-password':
        return isEmailSent ? "Enter the OTP code and your new password" : "Enter your email to reset your password";
      default:
        return "";
    }
  };

  const getButtonText = () => {
    switch (mode) {
      case "login":
        return "Sign In";
      case "signup":
        return "Create Account";
      case "forgot-password":
        return "Send Reset Code";
      case "reset-password":
        return "Reset Password";
      default:
        return "Continue";
    }
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-center mb-6">
        <img
          src="/logo.png"
          alt="Sleep Apnea Assessment Logo"
          className="h-16 w-auto object-contain mr-4"
        />
        <div className="text-center">
          <h2
            className="text-2xl font-bold mb-2"
            style={{ color: colors.heading }}
          >
            {getStepTitle()}
          </h2>
          <p
            className="text-sm"
            style={{ color: colors.text }}
          >
            {getStepSubtitle()}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {(mode === 'login' || mode === 'signup' || mode === 'forgot-password' || mode === 'reset-password') && (
          <div>
            <div className="relative">
              <FaEnvelope
                className="absolute left-4 top-1/2 transform -translate-y-1/2"
                size={18}
                style={{ color: colors.text + '80' }}
              />
              <input
                type="email"
                placeholder="Email address"
                className="w-full pl-12 pr-12 py-3 rounded-lg border-2 focus:outline-none transition-colors duration-200"
                style={{
                  backgroundColor: colors.input,
                  borderColor: getEmailBorderColor(),
                  color: colors.text,
                  minHeight: '44px',
                }}
                onFocus={() => setEmailFocused(true)}
                {...registerField("email", {
                  required: "Email is required",
                  validate: (value) => isValidEmail(value) || "Invalid email address",
                  onBlur: () => setEmailFocused(false),
                })}
              />
              <button
                type="button"
                className="absolute right-4 top-1/2 transform -translate-y-1/2 transition-colors duration-200"
                style={{ color: colors.primary }}
                title="Use temporary email"
              >
                <FaEnvelope size={16} />
              </button>
            </div>
            {formErrors.email && (
              <p className="text-sm mt-1" style={{ color: colors.error }}>{formErrors.email.message}</p>
            )}
          </div>
        )}

        {(mode === 'login' || mode === 'signup') && (
          <div>
            <div className="relative">
              <FaLock
                className="absolute left-4 top-1/2 transform -translate-y-1/2"
                size={18}
                style={{ color: colors.text + '80' }}
              />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                className="w-full pl-12 pr-12 py-3 rounded-xl border-2 focus:outline-none transition-colors duration-200"
                style={{
                  backgroundColor: colors.input,
                  borderColor: formErrors.password ? colors.error : colors.border,
                  color: colors.text,
                  minHeight: '44px',
                }}
                {...registerField("password", {
                  required: "Password is required",
                  minLength: {
                    value: 6,
                    message: "Password must be at least 6 characters",
                  },
                })}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 transition-colors duration-200"
                style={{ color: colors.text + '80' }}
              >
                {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
              </button>
            </div>
            {formErrors.password && (
              <p className="text-sm mt-1" style={{ color: colors.error }}>{formErrors.password.message}</p>
            )}

            {/* Forgot Password Link */}
            {mode === 'login' && (
              <div className="text-right mt-2">
                <button
                  type="button"
                  onClick={() => setMode('forgot-password')}
                  className="text-sm font-semibold hover:underline"
                  style={{ color: colors.primary }}
                >
                  Forgot your password?
                </button>
              </div>
            )}
          </div>
        )}

        {mode === 'reset-password' && (
          <div>
            <div className="relative">
              <FaLock
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Enter OTP code"
                className="w-full pl-12 pr-4 py-3 rounded-xl border-2 focus:outline-none transition-colors duration-200"
                style={{
                  backgroundColor: colors.input,
                  borderColor: formErrors.otp ? colors.error : colors.border,
                  color: colors.text,
                  minHeight: '44px',
                }}
                {...registerField("otp", {
                  required: "OTP code is required",
                  minLength: {
                    value: 6,
                    message: "OTP code must be 6 digits",
                  },
                  maxLength: {
                    value: 6,
                    message: "OTP code must be 6 digits",
                  },
                })}
              />
            </div>
            {formErrors.otp && (
              <p className="text-red-500 text-sm mt-1">{formErrors.otp.message}</p>
            )}
          </div>
        )}

        {mode === 'reset-password' && (
          <div>
            <div className="relative">
              <FaLock
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type={showNewPassword ? 'text' : 'password'}
                placeholder="New password"
                className="w-full pl-12 pr-12 py-3 rounded-xl border-2 focus:outline-none transition-colors duration-200"
                style={{
                  backgroundColor: colors.input,
                  borderColor: formErrors.newPassword ? colors.error : colors.border,
                  color: colors.text,
                  minHeight: '44px',
                }}
                {...registerField("newPassword", {
                  required: "New password is required",
                  minLength: {
                    value: 6,
                    message: "Password must be at least 6 characters",
                  },
                })}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                {showNewPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
              </button>
            </div>
            {formErrors.newPassword && (
              <p className="text-red-500 text-sm mt-1">{formErrors.newPassword.message}</p>
            )}
          </div>
        )}

        {(mode === 'signup' || mode === 'reset-password') && (
          <div>
            <div className="relative">
              <FaLock
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm password"
                className="w-full pl-12 pr-12 py-3 rounded-xl border-2 focus:outline-none transition-colors duration-200"
                style={{
                  backgroundColor: colors.input,
                  borderColor: formErrors.confirmPassword ? colors.error : colors.border,
                  color: colors.text,
                  minHeight: '44px',
                }}
                {...registerField("confirmPassword", {
                  required: "Please confirm your password",
                  validate: (value) =>
                    value === (mode === 'signup' ? password : newPassword) || 'Passwords do not match',
                })}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                {showConfirmPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
              </button>
            </div>
            {formErrors.confirmPassword && (
              <p className="text-red-500 text-sm mt-1">{formErrors.confirmPassword.message}</p>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 px-6 rounded-xl font-semibold text-white transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: colors.primaryButton }}
        >
          {isLoading ? "Loading..." : getButtonText()}
        </button>

      </form>

      {/* Google OAuth - Show for login and signup modes only */}
      {(mode === "login" || mode === "signup") && (
        <ContinueWith setErrors={setErrors} />
      )}

      {/* Mode Toggle */}
      <div className="text-center mt-4">
        {mode === "login" ? (
          <p className="text-sm">
            <span style={{ color: colors.text }}>Don't have an account? </span>
            <button
              onClick={() => setMode("signup")}
              className="font-semibold hover:underline"
              style={{ color: colors.primary }}
            >
              Sign up
            </button>
          </p>
        ) : mode === "signup" ? (
          <p className="text-sm">
            <span style={{ color: colors.text }}>Already have an account? </span>
            <button
              onClick={() => setMode("login")}
              className="font-semibold hover:underline"
              style={{ color: colors.primary }}
            >
              Sign in
            </button>
          </p>
        ) : mode === "forgot-password" ? (
          <p className="text-sm">
            <button
              onClick={() => setMode("login")}
              className="font-semibold hover:underline inline-flex items-center"
              style={{ color: colors.primary }}
            >
              <FaArrowLeft className="mr-2" />
              Back to Login
            </button>
          </p>
        ) : mode === "reset-password" ? (
          <p className="text-sm">
            <button
              onClick={() => setMode("forgot-password")}
              className="font-semibold hover:underline inline-flex items-center"
              style={{ color: colors.primary }}
            >
              <FaArrowLeft className="mr-2" />
              Back to Forgot Password
            </button>
          </p>
        ) : null}
      </div>

    </div>
  );
};

export default LoginSignUp;
